import { useEffect, useId, useRef, useState } from 'react'
import { Check, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { expiryDate } from '@/lib/process/allocation'
import { useReturnableInventory, useReturnInventory } from '@/hooks/useProcessSteps'
import type { ReturnDestination, ReturnableLot } from '@/api/processStepsApi'

const EXPLAIN: Record<ReturnDestination, string> = {
  return_to_inventory: 'The quantity entered goes back to inventory with its original status and expiry.',
  quarantine: 'The quantity entered goes back to inventory with status Quarantine.',
}

/** A lot row's entered quantity, plus what it means. */
interface Entry {
  lot: ReturnableLot
  raw: string
  qty: number
  /** Typed something that is not a returnable amount for this lot. */
  invalid: boolean
}

/**
 * The `[+]` beside an inventory line item — z8nrz7dt42.
 *
 * Renders nothing when there is nothing open to return, which is also what
 * keeps it off the Start dialog: before Start the allocator's ticks are
 * client-side only, so the ledger has no hold and the read comes back empty.
 * That is a truer gate than checking the mode, and it means the control
 * appears at ANY point the step is actually holding stock.
 */
export function InventoryReturnControl({
  submissionId,
  fieldId,
  requirementId,
  onReturned,
}: {
  submissionId: string
  fieldId: string
  requirementId: string
  /**
   * Told which lots were actually returned and how much, so the caller can
   * take it straight off the allocation it already holds in state — that
   * state is seeded from the execution once per modal-open (see
   * `ProcessStepField`'s `seededFor` guard) and does not re-seed itself when
   * this mutation's invalidation refetches it, which is what previously left
   * the "reserved" badge and the returnable list agreeing only after a full
   * page reload remounted the modal.
   *
   * Carries BOTH identifiers deliberately. The wire addresses a lot by its
   * database id, but `LotAllocations` is keyed by lot NUMBER, and handing the
   * caller only `lotId` meant the subtraction wrote `'0'` under a key no
   * allocation used: the returned quantity came off the ledger server-side
   * while the row's own "n of m reserved" chip sat unchanged, which is the
   * one place the operator looks to see that the return happened.
   */
  onReturned?: (lines: { lotId: string; lotNumber: string; qty: number }[]) => void
}) {
  const quarantineId = useId()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open) panelRef.current?.focus()
  }, [open])

  /**
   * Escape closes THIS panel, never the run behind it.
   *
   * A React `onKeyDown` is too late: Radix's DismissableLayer listens on the
   * DOCUMENT in the capture phase, so by the time the event reaches this
   * subtree the whole Start/Stop modal has already been told to dismiss —
   * `stopPropagation` on a bubbled handler cannot take that back.
   *
   * Capture on WINDOW runs before capture on document, so this sees the key
   * first. Radix only dismisses when the event is not already default-prevented
   * (see its `useEscapeKeydown` caller), which is exactly what this sets.
   */
  useEffect(() => {
    if (!open) return
    const onKeyDownCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      reset()
    }
    window.addEventListener('keydown', onKeyDownCapture, true)
    return () => window.removeEventListener('keydown', onKeyDownCapture, true)
    // `reset` is stable enough for this: it only ever clears local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  /** Which lots the operator has flagged, keyed by lot id. */
  const [quarantined, setQuarantined] = useState<Record<string, boolean>>({})
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const returnable = useReturnableInventory(submissionId, fieldId, requirementId, true)
  const returnInventory = useReturnInventory()

  const lots = returnable.data ?? []
  if (lots.length === 0) return null

  // Untouched rows default to the whole held quantity — returning everything is
  // the common case, and the operator edits down from there.
  const entries: Entry[] = lots.map((lot) => {
    const raw = typed[lot.lotId] ?? String(lot.qty)
    const qty = Number(raw)
    return {
      lot,
      raw,
      qty,
      invalid: raw.trim() !== '' && (!Number.isFinite(qty) || qty < 0 || qty > lot.qty),
    }
  })

  const selected = entries.filter((e) => !e.invalid && Number.isFinite(e.qty) && e.qty > 0)
  const anyInvalid = entries.some((e) => e.invalid)
  const total = selected.reduce((sum, e) => sum + e.qty, 0)

  /**
   * Whether ONE lot is being quarantined.
   *
   * Per lot, because the gate is a status on the lot itself: with several lots
   * in a return, a single "Quarantine" tick could not say which one it meant
   * and shut away every one of them. An expired lot has no choice — it cannot
   * go back as usable stock and the server answers 409 — so it is forced on and
   * its checkbox says why.
   */
  const isQuarantined = (lot: ReturnableLot) => quarantined[lot.lotId] === true || lot.expired
  const quarantinedSelected = selected.filter((e) => isQuarantined(e.lot))
  const anyQuarantined = quarantinedSelected.length > 0
  // The batch default still describes the whole act for the audit entry; the
  // lines carry the detail.
  const destination: ReturnDestination = quarantinedSelected.length === selected.length && anyQuarantined
    ? 'quarantine'
    : 'return_to_inventory'
  const needsReason = anyQuarantined && reason.trim() === ''

  function reset() {
    setError(null)
    setReason('')
    setTyped({})
    setQuarantined({})
  }

  function confirm() {
    setError(null)
    if (selected.length === 0) {
      setError('Enter a quantity to return.')
      return
    }
    if (needsReason) {
      setError('Enter why this is being quarantined.')
      return
    }
    returnInventory.mutate(
      {
        submissionId,
        fieldId,
        requirementId,
        payload: {
          destination,
          reason: reason.trim() || undefined,
          lines: selected.map((e) => ({
            lotId: e.lot.lotId,
            qty: e.qty,
            quarantine: isQuarantined(e.lot),
          })),
        },
      },
      {
        onSuccess: () => {
          onReturned?.(
            selected.map((e) => ({
              lotId: e.lot.lotId,
              lotNumber: e.lot.lotNumber,
              qty: e.qty,
            })),
          )
          setOpen(false)
          reset()
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not return this material.'),
      },
    )
  }

  function close() {
    setOpen(false)
    reset()
  }

  return (
    <>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        className='h-6 w-6 shrink-0'
        aria-label='Return unused quantity'
        // The row header is itself a toggle for the lot list; returning is a
        // separate action and must not open or close it.
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <Plus className='h-4 w-4' />
      </Button>
      {/* A centred panel with its own scrim, NOT a nested Radix Dialog: this
          renders inside the Start/End Process Step modal, and nesting dialog
          roots makes the two fight over the focus trap — the same reason the
          undo flow swaps the modal body instead of opening a second dialog.
          Rendering in place (rather than portalling to document.body) is what
          keeps it clickable, since a Radix modal sets `pointer-events: none` on
          the body and traps focus in its own subtree.

          It replaced an anchored popover, which hung off the `[+]` at the far
          right of a row and overlaid the very lot list it was about. Returning
          stock is a decision with consequences — quarantine re-gates the whole
          lot — so it gets the centre of the screen and a dimmed background
          rather than a corner.

          Three zones: a fixed header, a scrolling body, a pinned footer, so
          Confirm stays reachable however many lots come back.

          The scrim covers the STEP MODAL, not the viewport, and that is what it
          is meant to do. `DialogContent` is translated
          (`translate-x-[-50%] translate-y-[-50%]`), and a transform makes an
          element the containing block for its `fixed` descendants — so `inset-0`
          resolves to the dialog's box. Kept as `fixed` rather than `absolute`
          precisely because that resolution is predictable: the transformed
          dialog is the containing block wherever this row sits, while
          `absolute` would hunt for the nearest positioned ancestor.

          No `aria-modal` on it: focus is not trapped and the rest of the dialog
          is not inert, so claiming it would mislead a screen reader. */}
      {open && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
          role='dialog'
          aria-label='Return unused quantity'
          onClick={close}
        >
          <div
            ref={panelRef}
            // Focused on open so the panel owns the keyboard immediately —
            // the operator lands in the thing they just opened, and Tab starts
            // inside it. Escape itself no longer depends on this: it is caught
            // on `window` in the capture phase (see the effect above), ahead of
            // the document listener Radix uses to dismiss the run modal.
            tabIndex={-1}
            className='flex max-h-[85vh] w-[min(28rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl outline-none'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='border-b border-gray-100 px-4 py-3'>
              <p className='text-sm font-semibold text-gray-900'>Return unused quantity</p>
              <p className='mt-0.5 text-[11px] text-muted-foreground'>
                Hand back what this step did not use. The rest stays held until it is signed off.
              </p>
            </div>

        <div className='min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3'>
          {/* A bordered, divided list rather than free-floating rows: it is a
              table of what the step is holding, and the rule down the side is
              what keeps the quantity inputs reading as one column. */}
          <ul className='divide-y divide-gray-100 overflow-hidden rounded-md border border-gray-200'>
            {entries.map((e) => (
              <li key={e.lot.lotId} className='px-2.5 py-2'>
                <div className='flex items-center gap-3'>
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-xs font-medium text-gray-900'>{e.lot.lotNumber}</p>
                    <p className='text-[11px] text-muted-foreground'>
                      {e.lot.qty} held
                      {/* Sliced, never parsed — `new Date(iso).toLocaleDateString()`
                          shifts the stored date by a day in western timezones, so
                          this row and the allocator's own `exp` disagreed about
                          the same lot. Shared helper, one answer. */}
                      {e.lot.expiryDate ? ` · expires ${expiryDate(e.lot.expiryDate)}` : ''}
                      {e.lot.expired ? ' · expired' : ''}
                    </p>
                  </div>
                  <Input
                    type='number'
                    inputMode='decimal'
                    min={0}
                    max={e.lot.qty}
                    value={e.raw}
                    onChange={(ev) => setTyped((t) => ({ ...t, [e.lot.lotId]: ev.target.value }))}
                    aria-label={`Quantity to return from lot ${e.lot.lotNumber}`}
                    aria-invalid={e.invalid}
                    className={cn(
                      'h-7 w-20 shrink-0 text-right text-xs tabular-nums',
                      e.invalid && 'border-destructive',
                    )}
                  />
                </div>
                {/* Per LOT, because the gate is a status on the lot: one tick
                    for the whole return could not say which lot it meant. Named
                    with the lot number so the checkbox reads as a decision about
                    THIS container, both on screen and to a screen reader. */}
                <div className='mt-1.5 flex items-start gap-2 pl-0.5'>
                  <Checkbox
                    id={`${quarantineId}-${e.lot.lotId}`}
                    checked={isQuarantined(e.lot)}
                    disabled={e.lot.expired}
                    onCheckedChange={(v) =>
                      setQuarantined((q) => ({ ...q, [e.lot.lotId]: v === true }))
                    }
                    className='mt-0.5'
                  />
                  <Label
                    htmlFor={`${quarantineId}-${e.lot.lotId}`}
                    className='cursor-pointer text-[11px] font-normal text-gray-600'
                  >
                    Quarantine {e.lot.lotNumber}
                    {e.lot.expired && (
                      <span className='block text-[11px] text-amber-700'>
                        Required — an expired lot cannot go back with its original status.
                      </span>
                    )}
                  </Label>
                </div>
              </li>
            ))}
          </ul>

          {anyInvalid && (
            <p className='text-xs text-destructive'>
              A quantity must be between 0 and what the lot still holds.
            </p>
          )}

          {/* The consequence lines sit together in one tinted block, so what
              Confirm is about to do reads as a single statement instead of
              paragraphs competing with the controls above them. Both
              destinations are stated when the return is mixed, because that is
              genuinely what is about to happen. */}
          <div
            className={cn(
              'space-y-1.5 rounded-md px-2.5 py-2',
              anyQuarantined ? 'bg-amber-50' : 'bg-gray-50',
            )}
          >
            <p className='text-[11px] leading-relaxed text-gray-600'>
              {quarantinedSelected.length < selected.length
                ? EXPLAIN.return_to_inventory
                : EXPLAIN.quarantine}
            </p>
            {anyQuarantined && quarantinedSelected.length < selected.length && (
              <p className='text-[11px] leading-relaxed text-gray-600'>
                {quarantinedSelected.map((e) => e.lot.lotNumber).join(', ')} goes to Quarantine
                instead.
              </p>
            )}
            {/* The gate lives on the LOT, not on a quantity, so quarantining any
                amount re-gates every remaining unit of it — including stock held
                by other steps. Accepted behaviour, but the operator has to be
                told: the number they typed is not the extent of what they are
                changing. */}
            {anyQuarantined && (
              <p className='text-[11px] font-medium leading-relaxed text-amber-800'>
                Quarantine applies to the whole of{' '}
                {quarantinedSelected.length === 1
                  ? `lot ${quarantinedSelected[0].lot.lotNumber}`
                  : 'each lot ticked above'}
                , including units held by other steps — not only the quantity entered.
              </p>
            )}
          </div>

          {anyQuarantined && (
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='Why is this being quarantined?'
              className='text-xs'
              rows={2}
            />
          )}

          {error && <p className='text-xs text-destructive'>{error}</p>}
        </div>

            <div className='flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3'>
              <span className='text-[11px] tabular-nums text-muted-foreground'>
                Returning {total} of {lots.reduce((sum, l) => sum + l.qty, 0)}
              </span>
              <div className='flex gap-2'>
                <Button type='button' variant='outline' size='sm' onClick={close}>
                  Cancel
                </Button>
                <Button
                  type='button'
                  size='sm'
                  className='gap-1.5'
                  disabled={
                    returnInventory.isPending || anyInvalid || selected.length === 0 || needsReason
                  }
                  onClick={confirm}
                >
                  <Check className='h-3.5 w-3.5' />
                  {returnInventory.isPending ? 'Confirming…' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
